// src/components/reports/CompanyTrendReport/TrendChart.tsx
// 既存のインポート文はそのまま
import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { Box, Paper, Typography } from '@mui/material';
import { CompanyTrendReportItem, formatPeriodLabel, formatCurrency } from '../../../../types/sales';

interface TrendChartProps {
  data: CompanyTrendReportItem[];
  title: string;
}

export const TrendChart: React.FC<TrendChartProps> = ({ data, title }) => {
  // データをグラフ用に整形 - この部分はそのまま
  const chartData = data.map(item => ({
    ...item,
    period: formatPeriodLabel(item.period),
    total_sales: Number(item.total_sales),
    final_profit: Number(item.final_profit),
    previous_sales: Number(item.previous_sales) || 0,
    project_count: Number(item.project_count)
  }));

  // カスタムツールチップ - この部分はそのまま
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper elevation={3} sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={`tooltip-${index}`}
              variant="body2"
              sx={{
                color: entry.color,
                display: 'flex',
                justifyContent: 'space-between',
                mb: 0.5
              }}
            >
              <span style={{ marginRight: '12px' }}>{entry.name}:</span>
              <span style={{ fontWeight: 'bold' }}>
                {entry.dataKey.includes('rate')
                  ? `${entry.value.toFixed(1)}%`
                  : entry.dataKey === 'project_count'
                    ? `${entry.value}件`
                    : formatCurrency(entry.value)
                }
              </span>
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          height: 'auto',
          minHeight: 400,
          bgcolor: 'background.paper',
          overflow: 'visible' // これを追加
        }}
      >
        {/* 固定高さのコンテナを使用 */}
        <Box sx={{ width: '100%', height: 400 }}>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="period"
                scale="band"
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                tickFormatter={(value) => `¥${(value / 10000).toFixed(0)}万`}
                label={{
                  value: '金額（円）',
                  angle: -90,
                  position: 'insideLeft'
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={(value) => `${value}件`}
                label={{
                  value: '件数',
                  angle: 90,
                  position: 'insideRight'
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="total_sales"
                name="売上金額"
                barSize={20}
                fill="#8884d8"
              />
              <Bar
                yAxisId="left"
                dataKey="final_profit"
                name="最終利益"
                barSize={20}
                fill="#ffc658"
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="final_profit"
                name="最終利益"
                stroke="#82ca9d"
                activeDot={{ r: 8 }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="previous_sales"
                name="前年同期売上"
                stroke="#ffc658"
                strokeDasharray="5 5"
              />
              <Bar
                yAxisId="right"
                dataKey="project_count"
                name="工事件数"
                barSize={10}
                fill="#ff7300"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Box>
  );
};